import { useStatusSummary } from "@/api/incidentio";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/utils";
import { AlertTriangle, ExternalLink } from "lucide-react";
import React from "react";

interface StatusBannerProps {
  className?: string;
}

export const StatusBanner: React.FC<StatusBannerProps> = ({
  className = "",
}) => {
  const { data: statusSummary, isLoading, isError } = useStatusSummary();

  // Don't render if no data, still loading, or error
  if (isLoading || isError || !statusSummary) {
    return null;
  }

  // Check for ongoing incidents
  const hasIncidents = statusSummary.ongoing_incidents.length > 0;
  const hasMaintenances = statusSummary.in_progress_maintenances.length > 0;

  // Don't render if no active incidents or maintenances
  if (!hasIncidents && !hasMaintenances) {
    return null;
  }

  // Show the first ongoing incident if available, otherwise the first maintenance
  const activeItem = hasIncidents
    ? statusSummary.ongoing_incidents[0]
    : statusSummary.in_progress_maintenances[0];

  return (
    <div
      className={cn(
        "w-full bg-lc-blocked/10 dark:bg-spectrum-red-dark/20 border-b border-lc-blocked/30 dark:border-spectrum-red-dark py-4 px-4 md:px-8",
        className,
      )}
    >
      <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
        <div className="flex-1 min-w-0 w-full md:w-auto">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-700 dark:text-lc-blocked flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-red-900 dark:text-lc-blocked mb-1">
                {activeItem.name}
              </div>
              {activeItem.last_update_message && (
                <div className="text-sm text-red-900 dark:text-lc-blocked md:block hidden">
                  {activeItem.last_update_message}
                </div>
              )}
            </div>
          </div>
        </div>

        <a
          href={activeItem.url}
          className={cn(
            buttonVariants({ variant: "outline", size: "sm" }),
            "w-full md:w-auto flex-shrink-0 border-red-700 text-red-700 bg-lc-blocked/10 hover:bg-red-100 dark:border-lc-blocked/30 dark:text-lc-blocked dark:bg-spectrum-red-dark/20 dark:hover:bg-red-900",
          )}
          target="_blank"
          rel="noopener noreferrer"
        >
          View status page
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};
